/**
 * useAdRedirect - Monetization hook for download buttons
 * 
 * Logic:
 *  - Click 1: Open ad URL in new tab → show "Click again to continue" toast
 *  - Click 2: Open ad URL again in new tab → show "One more click to download" toast
 *  - Click 3: Execute the actual download action → reset counter
 * 
 * Each download action has its own independent counter tracked by a unique key.
 */

import { useRef, useCallback, useState } from 'react';

const AD_URL = 'https://www.profitablecpmratenetwork.com/ffevda195?key=8041eb5f8a219d4a8fc1c05e8e7fe485';
const AD_CLICKS_REQUIRED = 2; // number of ad opens before download

/**
 * Opens the ad URL in a new tab (with popup-blocker bypass attempt)
 */
const openAdUrl = () => {
  try {
    const w = window.open(AD_URL, '_blank', 'noopener,noreferrer');
    if (!w) {
      // fallback if popup blocked
      const a = document.createElement('a');
      a.href = AD_URL;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } catch (e) {
    console.warn('Ad URL open failed:', e);
  }
};

/**
 * Hook: useAdRedirect
 * 
 * @returns {{ withAd: (key: string, action: () => void) => () => void, toast: string | null }}
 * 
 * Usage:
 *   const { withAd } = useAdRedirect();
 *   <button onClick={withAd('qr-png', downloadPNG)}>Download PNG</button>
 */
export const useAdRedirect = () => {
  const counters = useRef({});
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  /**
   * withAd(key, action) — wrap a download action with ad gate
   * Returns an onClick handler
   */
  const withAd = useCallback((key, action) => {
    return () => {
      const current = counters.current[key] || 0;

      if (current < AD_CLICKS_REQUIRED) {
        // Open ad
        openAdUrl();
        counters.current[key] = current + 1;

        if (current === 0) {
          showToast('✅ Ad opened! Click the button once more to continue...');
        } else {
          showToast('⏳ Almost there! Click one more time to download.');
        }
      } else {
        // Execute actual download
        counters.current[key] = 0; // reset for next download
        setToast(null);
        if (typeof action === 'function') action();
      }
    };
  }, [showToast]);

  return { withAd, toast };
};

/**
 * AdToast - floating toast notification component
 */
export const AdToast = ({ message }) => {
  if (!message) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: '28px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'linear-gradient(135deg, #0575fb, #0461d2)',
      color: '#fff',
      padding: '14px 28px',
      borderRadius: '50px',
      fontSize: '0.92rem',
      fontWeight: 600,
      boxShadow: '0 8px 32px rgba(5, 117, 251, 0.38)',
      zIndex: 99999,
      whiteSpace: 'nowrap',
      letterSpacing: '0.01em',
      animation: 'adToastIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
      pointerEvents: 'none',
    }}>
      {message}
      <style>{`
        @keyframes adToastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(20px) scale(0.93); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );
};

export default useAdRedirect;
